/**
 * Erros de `POST /orders/{id}/requests` (troca, checklist e demais tipos por item).
 * Contrato em b2b-order-portal-api, Endpoints/OrderSupportRequestEndpoints.cs.
 */

/**
 * Falha ao enviar um anexo direto ao S3 (pedido da URL de envio ou o envio em si), antes de criar a solicitação.
 * `retryable` = rede, S3 ou API fora do ar: "Tentar novamente" resolve; senão é o arquivo (formato, tamanho).
 */
export class AttachmentUploadError extends Error {
  constructor(
    readonly fileName: string,
    message: string,
    readonly retryable: boolean
  ) {
    super(message);
    this.name = "AttachmentUploadError";
  }
}

/** Campos do formulário que recebem erro de validação no próprio campo. */
export type SupportRequestField = "description" | "quantity" | "attachments";

export type SupportRequestError =
  /** 400 do FluentValidation: `{ message, errors: [{ field, message }] }`. */
  | {
      kind: "validation";
      fieldErrors: Partial<Record<SupportRequestField, string>>;
      /** Conferência: erro de cada item enviado, pela posição em `items` ("Items[1].ReceivedQuantity" → 1). */
      itemErrors: Record<number, string>;
      /** Erros de campos que não estão no formulário (tipo, item) — mostrados no topo. */
      generalErrors: string[];
    }
  /** 400 de regra: tipo inválido ou item que não pertence ao pedido. */
  | { kind: "rule"; message: string }
  | { kind: "notFound"; message: string }
  /**
   * 409: este mesmo envio (`clientRequestId`) já foi registrado — a resposta anterior se perdeu —, ou a divergência
   * que o pedido já tem (uma por pedido). `zendeskTicketId` é o ticket existente.
   */
  | { kind: "alreadyOpen"; message: string; zendeskTicketId: number | null }
  /** 503: o Zendesk não respondeu e nada foi gravado — pode tentar de novo. */
  | { kind: "unavailable"; message: string }
  /** Rede ou status inesperado — reenviar é seguro, o back reaproveita o ticket já aberto. */
  | { kind: "unexpected"; message: string };

interface ErrorBody {
  message?: unknown;
  errors?: unknown;
  zendeskTicketId?: unknown;
}

interface ApiErrorLike {
  response?: {
    status?: number;
    data?: ErrorBody | null;
  };
}

const NOT_FOUND_MESSAGE = "Pedido não encontrado. Verifique se o link ainda é válido.";
const UNAVAILABLE_MESSAGE = "Não foi possível abrir a solicitação agora. Tente novamente em instantes.";
const UNEXPECTED_MESSAGE = "Não foi possível enviar a solicitação. Verifique sua conexão e tente novamente.";

const FORM_FIELDS: SupportRequestField[] = ["description", "quantity", "attachments"];
const ATTACHMENTS_TOO_LARGE_MESSAGE = "Os anexos ultrapassam o tamanho permitido. Envie arquivos menores.";

// O FluentValidation devolve o nome da propriedade em PascalCase ("Description") e, em coleções,
// com o índice ("Attachments[0]").
function toFormField(field: unknown): SupportRequestField | null {
  if (typeof field !== "string") return null;
  const name = field.toLowerCase().replace(/\[\d+\].*$/, "");
  return FORM_FIELDS.find((formField) => formField === name) ?? null;
}

function toItemIndex(field: unknown): number | null {
  if (typeof field !== "string") return null;
  const match = /^items\[(\d+)\]/i.exec(field);
  return match ? Number(match[1]) : null;
}

function messageFrom(data: ErrorBody | null | undefined, fallback: string): string {
  return typeof data?.message === "string" && data.message.trim() ? data.message : fallback;
}

export function parseSupportRequestError(error: unknown): SupportRequestError {
  if (error instanceof AttachmentUploadError) {
    return error.retryable
      ? { kind: "unexpected", message: error.message }
      : { kind: "validation", fieldErrors: { attachments: error.message }, itemErrors: {}, generalErrors: [] };
  }
  const response = (error as ApiErrorLike)?.response;
  const data = response?.data;

  switch (response?.status) {
    case 400: {
      const errors: unknown[] = Array.isArray(data?.errors) ? data.errors : [];
      if (errors.length === 0) {
        return { kind: "rule", message: messageFrom(data, "Não foi possível registrar a solicitação.") };
      }
      const fieldErrors: Partial<Record<SupportRequestField, string>> = {};
      const itemErrors: Record<number, string> = {};
      const generalErrors: string[] = [];
      for (const entry of errors as { field?: unknown; message?: unknown }[]) {
        const message = typeof entry?.message === "string" ? entry.message : null;
        if (!message) continue;
        const itemIndex = toItemIndex(entry.field);
        const field = toFormField(entry.field);
        // Sem `??=`: o SWC do projeto gera código inválido para atribuição lógica com índice calculado.
        if (itemIndex !== null) {
          if (!itemErrors[itemIndex]) itemErrors[itemIndex] = message;
        } else if (field) {
          if (!fieldErrors[field]) fieldErrors[field] = message;
        } else {
          generalErrors.push(message);
        }
      }
      return { kind: "validation", fieldErrors, itemErrors, generalErrors };
    }
    case 404:
      return { kind: "notFound", message: NOT_FOUND_MESSAGE };
    // Limite de corpo do servidor/proxy, antes de chegar na validação da API.
    case 413:
      return {
        kind: "validation",
        fieldErrors: { attachments: ATTACHMENTS_TOO_LARGE_MESSAGE },
        itemErrors: {},
        generalErrors: [],
      };
    case 409:
      return {
        kind: "alreadyOpen",
        message: messageFrom(data, "Já existe uma solicitação aberta."),
        zendeskTicketId: typeof data?.zendeskTicketId === "number" ? data.zendeskTicketId : null,
      };
    case 503:
      return { kind: "unavailable", message: messageFrom(data, UNAVAILABLE_MESSAGE) };
    default:
      return { kind: "unexpected", message: UNEXPECTED_MESSAGE };
  }
}

/** 503, rede e status inesperado: o mesmo envio pode ser repetido sem duplicar o ticket. */
export function isRetryableSupportRequestError(error: SupportRequestError): boolean {
  return error.kind === "unavailable" || error.kind === "unexpected";
}
